import type { Shift } from "@ots-frontend/ots-data";
import type { ReactNode } from "react";
import type { OtsButtonVariant } from "../components/OtsButton";
import type { CostingProgress } from "../components/OtsCostingProgressHost";
import type { OtsCostingButtonStatus } from "../components/OtsCostingButton";
import type { ActivityEntry } from "./driver-activity-view-model";

export type DriverActivityAction = {
  id: string;
  label: string;
  /**
   * `single` acts on exactly one ticket and is also offered in the inspector; `any` takes the whole selection;
   * `optional` also runs with nothing selected, as creating a shift or ticket does.
   */
  scope: "single" | "any" | "optional";
  /**
   * `tools` actions are folded into the Tools menu. `create` actions make something new from what is selected:
   * they are the header's New menu and the New section of a ticket's right-click menu.
   */
  group?: "primary" | "tools" | "create";
  /**
   * Section of the tools menu (the selection bar's Tools button and a ticket's right-click menu), such as
   * "Post Holding"; see `DRIVER_ACTIVITY_TOOL_GROUPS`. Primary actions default to "Ticket Overview", tools to "Other".
   */
  toolGroup?: string;
  variant?: OtsButtonVariant;
  /** Offered even when the workspace is read-only. */
  readOnly?: boolean;
  /** Whether it applies to these tickets, e.g. a delivery comment needs a delivery; disabled when not. */
  isEnabled?: (entries: ActivityEntry[]) => boolean;
  /** Whether to offer it at all for these tickets, e.g. Compliance only where a program applies; hidden when not. */
  isVisible?: (entries: ActivityEntry[]) => boolean;
  /** Shown in place of the label where room is short, as in the inspector's action row; the label becomes its tooltip. */
  icon?: ReactNode;
};

/** The legacy viewer's footer buttons. Hosts pass their own list to add Tools entries. */
export const DRIVER_ACTIVITY_ACTIONS: DriverActivityAction[] = [
  { id: "view", label: "View", scope: "single", readOnly: true },
  { id: "edit", label: "Edit", scope: "single" },
  { id: "map", label: "Map", scope: "any", readOnly: true },
  { id: "validate", label: "Validate", scope: "any", variant: "tonal" },
  { id: "delete", label: "Delete", scope: "any", variant: "destructive" },
];

/** What a shift row can do: edit its shift, or start a new one after it (or around activity outside any shift). */
export type ActivityShiftActionId = "edit-shift" | "new-shift";

/** The shift block a shift-row action was run from. */
export type ActivityShiftTarget = {
  /** Missing for activity recorded outside any shift. */
  shift?: Shift;
  entries: ActivityEntry[];
};

/**
 * A self-contained set of actions: what they are, how to run them, and any dialogs they open. Hosts compose
 * providers (their own page actions, the shared ticket tools, …) with {@link combineActionProviders} and hand
 * the result to the workspace's `actionProvider`.
 */
export type DriverActivityActionProvider = {
  actions: DriverActivityAction[];
  /** Runs one of `actions`; false for an id this provider does not own. */
  handleAction: (actionId: string, entries: ActivityEntry[]) => boolean;
  /** Dialogs the actions open; the workspace renders them inside its theme. */
  dialogs?: ReactNode;
  /** Buttons on each shift's start row, and what they do. */
  shiftActions?: {
    ids: ActivityShiftActionId[];
    handle: (actionId: ActivityShiftActionId, target: ActivityShiftTarget) => void;
  };
  /** Costing shifts from their end rows. */
  shiftCost?: ActivityShiftCost;
};

/** Where one shift's costing stands: running, just done or failed, and whether its figures are an unsaved preview. */
export type ActivityShiftCostState = {
  /** Missing once a finished costing has had its moment; `preview` then stays. */
  status?: Exclude<OtsCostingButtonStatus, "idle">;
  progress?: CostingProgress | null;
  startedAt?: number;
  /** The figures on screen were worked out but not saved. */
  preview?: boolean;
  error?: string;
};

/** Costing a shift from its end row, and where each shift's costing stands. */
export type ActivityShiftCost = {
  stateOf: (shiftId: string) => ActivityShiftCostState | undefined;
  run: (shift: Shift) => void;
  /** The costs will only be previewed, not saved. */
  preview?: boolean;
};

/** A provider from actions and a handler per action id. */
export const createActionProvider = (
  actions: DriverActivityAction[],
  handlers: Partial<Record<string, (entries: ActivityEntry[]) => void>>,
  dialogs?: ReactNode,
): DriverActivityActionProvider => ({
  actions,
  handleAction: (actionId, entries) => {
    const handler = handlers[actionId];
    if (!handler) return false;
    handler(entries);
    return true;
  },
  dialogs,
});

/** One provider from several, in order: actions are listed in that order and the first that owns an id runs it. */
export const combineActionProviders = (...providers: (DriverActivityActionProvider | undefined | false)[]): DriverActivityActionProvider => {
  const present = providers.filter((provider): provider is DriverActivityActionProvider => !!provider);
  return {
    actions: present.flatMap((provider) => provider.actions),
    handleAction: (actionId, entries) => present.some((provider) => provider.handleAction(actionId, entries)),
    dialogs: present.some((provider) => provider.dialogs != null)
      ? <>{present.map((provider, index) => <span key={index} className="contents">{provider.dialogs}</span>)}</>
      : undefined,
    // Shift rows take their buttons from the first provider that offers any.
    shiftActions: present.find((provider) => provider.shiftActions?.ids.length)?.shiftActions,
    shiftCost: present.find((provider) => provider.shiftCost)?.shiftCost,
  };
};
